// src/views/incidencias/EvidenciasListado.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiExternalLink,
  FiFileText,
  FiDownload,
  FiX,
  FiTrash2,
} from "react-icons/fi";

// ✅ usa tu services/api (token + api-key) — NO rompe nada
import {
  getIncidencias,
  deleteIncidencia,
  generateIncidenciaPdf,
} from "../../services/api";

// Export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function EvidenciasListado() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // UX
  const [deletingId, setDeletingId] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);

  // =========================
  // ✅ AUTO-REFRESH (AWS style)
  // =========================
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshEvery, setRefreshEvery] = useState(30); // seconds: 10 / 30 / 60
  const inFlightRef = useRef(false);
  const intervalRef = useRef(null);

  // ===== filtros (inputs date -> YYYY-MM-DD)
  const today = new Date().toISOString().slice(0, 10);
  const firstDayMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10);

  const [q, setQ] = useState("");
  const [fechaIni, setFechaIni] = useState(firstDayMonth);
  const [fechaFin, setFechaFin] = useState(today);

  const [municipio, setMunicipio] = useState("");
  const [oficina, setOficina] = useState("");
  const [tipo, setTipo] = useState("");
  const [riesgo, setRiesgo] = useState("");
  const [cargo, setCargo] = useState("");
  const [reportadoPor, setReportadoPor] = useState("");

  const [soloConImagenes, setSoloConImagenes] = useState(false);
  const [soloConFirma, setSoloConFirma] = useState(false);
  const [soloConPdf, setSoloConPdf] = useState(false);

  // ===== carga (con anti-doble-invocación)
  const cargar = async () => {
    if (inFlightRef.current) return; // ✅ evita duplicados (intervalo + click)
    inFlightRef.current = true;

    try {
      setLoading(true);
      setErr("");
      const res = await getIncidencias();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setErr("No se pudieron cargar las incidencias/evidencias.");
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  // carga inicial
  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Auto-refresh: interval + pausa cuando la pestaña no está visible
  useEffect(() => {
    const clear = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };

    const start = () => {
      clear();
      if (!autoRefresh) return;

      intervalRef.current = setInterval(() => {
        // si no es visible, no gastes
        if (document.visibilityState !== "visible") return;
        cargar();
      }, Math.max(5, Number(refreshEvery)) * 1000);
    };

    start();

    const onVisibility = () => {
      // si regresa a visible, refresca una vez inmediato
      if (document.visibilityState === "visible" && autoRefresh) {
        cargar();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshEvery]);

  // ===== opciones dinámicas
  const options = useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();
    return {
      municipios: uniq(rows.map((r) => r.municipio)),
      oficinas: uniq(rows.map((r) => r.oficina)),
      tipos: uniq(rows.map((r) => r.tipoIncidencia)),
      riesgos: uniq(rows.map((r) => r.nivelRiesgo)),
      cargos: uniq(rows.map((r) => r.cargo)),
      reportados: uniq(rows.map((r) => r.reportadoPor)),
    };
  }, [rows]);

  // =========================
  // TIMEZONE HELPERS (Cancún)
  // =========================
  function ymdInTz(dateInput, timeZone = "America/Cancun") {
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);

    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;

    return y && m && day ? `${y}-${m}-${day}` : null; // YYYY-MM-DD
  }

  // ✅ filtro por rango: compara por DÍA en Cancún
  const inRange = (row) => {
    const raw = row?.createdAt || row?.fechaReporte || row?.fecha;
    if (!raw) return false;

    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      if (fechaIni && raw < fechaIni) return false;
      if (fechaFin && raw > fechaFin) return false;
      return true;
    }

    const ymd = ymdInTz(raw, "America/Cancun");
    if (!ymd) return false;

    if (fechaIni && ymd < fechaIni) return false;
    if (fechaFin && ymd > fechaFin) return false;

    return true;
  };

  // ===== conteos / flags
  const countImages = (r) => {
    if (typeof r.evidenciasCount === "number") return r.evidenciasCount;
    if (Array.isArray(r.evidencias)) return r.evidencias.length;
    if (Array.isArray(r.evidenciasKeys)) return r.evidenciasKeys.length;
    return 0;
  };

  const hasFirma = (r) => !!(r.firmaDataUrl || r.firmaKey || r.firma);
  const hasPdf = (r) => !!(r.pdfKey || r.pdfUrl);

  const textBlob = (r) =>
    [
      r.id,
      r.lugarReporte,
      r.municipio,
      r.oficina,
      r.tipoIncidencia,
      r.nivelRiesgo,
      r.cargo,
      r.reportadoPor,
      r.nombreReportante,
      r.descripcion,
      r.intervino,
      r.evidenciaTexto,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  // ===== data filtrada
  const filtered = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();

    return rows
      .filter((r) => inRange(r))
      .filter((r) => (municipio ? String(r.municipio || "") === municipio : true))
      .filter((r) => (oficina ? String(r.oficina || "") === oficina : true))
      .filter((r) => (tipo ? String(r.tipoIncidencia || "") === tipo : true))
      .filter((r) => (riesgo ? String(r.nivelRiesgo || "") === riesgo : true))
      .filter((r) => (cargo ? String(r.cargo || "") === cargo : true))
      .filter((r) => (reportadoPor ? String(r.reportadoPor || "") === reportadoPor : true))
      .filter((r) => (soloConImagenes ? countImages(r) > 0 : true))
      .filter((r) => (soloConFirma ? hasFirma(r) : true))
      .filter((r) => (soloConPdf ? hasPdf(r) : true))
      .filter((r) => (qq ? textBlob(r).includes(qq) : true))
      .sort((a, b) => {
        const ta = new Date(a.createdAt || a.fechaReporte || a.fecha || 0).getTime() || 0;
        const tb = new Date(b.createdAt || b.fechaReporte || b.fecha || 0).getTime() || 0;
        return tb - ta;
      });
  }, [
    rows,
    q,
    fechaIni,
    fechaFin,
    municipio,
    oficina,
    tipo,
    riesgo,
    cargo,
    reportadoPor,
    soloConImagenes,
    soloConFirma,
    soloConPdf,
  ]);

  const resumen = useMemo(() => {
    const total = filtered.length;
    const conImg = filtered.filter((r) => countImages(r) > 0).length;
    const conFirma = filtered.filter((r) => hasFirma(r)).length;
    const conPdf = filtered.filter((r) => hasPdf(r)).length;

    const porRiesgo = {};
    for (const r of filtered) {
      const k = r.nivelRiesgo || "N/D";
      porRiesgo[k] = (porRiesgo[k] || 0) + 1;
    }

    return { total, conImg, conFirma, conPdf, porRiesgo };
  }, [filtered]);

  // ===== PDF
  const abrirPdf = async (id) => {
    try {
      const res = await generateIncidenciaPdf(id);
      const url = res.data?.url;
      if (!url) return alert("No se obtuvo URL de PDF.");
      window.open(url, "_blank");
    } catch (e) {
      console.error(e);
      alert("No se pudo abrir el PDF (revisa Lambda/API).");
    }
  };

  // ===== eliminar
  const eliminar = async (id) => {
    const ok = window.confirm(
      `¿Seguro que deseas eliminar la incidencia ${id}?\nEsta acción no se puede deshacer.`
    );
    if (!ok) return;

    try {
      setDeletingId(id);
      await deleteIncidencia(id);
      await cargar();
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar la incidencia (revisa Lambda/API).");
    } finally {
      setDeletingId("");
    }
  };

  // ===== export Excel
  const exportarExcel = () => {
    const data = filtered.map((r) => ({
      ID: r.id,
      Fecha: fmt(r.createdAt || r.fechaReporte || r.fecha),
      Municipio: r.municipio || "",
      Oficina: r.oficina || "",
      Lugar: r.lugarReporte || "",
      Tipo: r.tipoIncidencia || "",
      Riesgo: r.nivelRiesgo || "",
      Cargo: r.cargo || "",
      ReportadoPor: r.reportadoPor || "",
      NombreReportante: r.nombreReportante || "",
      Imagenes: countImages(r),
      Firma: hasFirma(r) ? "SI" : "NO",
      PDF: hasPdf(r) ? "SI" : "NO",
      Descripcion: r.descripcion || "",
      Intervino: r.intervino || "",
      EvidenciaTexto: r.evidenciaTexto || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Evidencias");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "reporte_evidencias.xlsx");
  };

  // ===== export PDF
  const exportarPDF = () => {
    const doc = new jsPDF("landscape");
    doc.setFontSize(14);
    doc.text("Reporte de Evidencias (Incidencias)", 14, 14);
    doc.setFontSize(9);
    doc.text(`Rango: ${fechaIni} a ${fechaFin} | Total: ${resumen.total}`, 14, 20);

    const body = filtered.map((r) => [
      r.id,
      fmt(r.createdAt || r.fechaReporte || r.fecha),
      `${r.oficina || ""} - ${r.municipio || ""}`,
      r.tipoIncidencia || "",
      r.nivelRiesgo || "",
      r.reportadoPor || "",
      String(countImages(r)),
      hasFirma(r) ? "SI" : "NO",
    ]);

    autoTable(doc, {
      startY: 24,
      head: [["ID", "Fecha", "Ubicación", "Tipo", "Riesgo", "Reportado por", "Imgs", "Firma"]],
      body,
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 },
    });

    doc.save("reporte_evidencias.pdf");
  };

  // ===== limpiar filtros
  const limpiar = () => {
    setQ("");
    setFechaIni(firstDayMonth);
    setFechaFin(today);
    setMunicipio("");
    setOficina("");
    setTipo("");
    setRiesgo("");
    setCargo("");
    setReportadoPor("");
    setSoloConImagenes(false);
    setSoloConFirma(false);
    setSoloConPdf(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Evidencias</h1>
          
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {/* ✅ Auto-refresh controls (AWS style) */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white">
            <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Auto-refresh
            </label>

            <select
              value={refreshEvery}
              onChange={(e) => setRefreshEvery(Number(e.target.value))}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-slate-50"
              disabled={!autoRefresh}
              title="Intervalo"
            >
              <option value={10}>10s</option>
              <option value={30}>30s</option>
              <option value={60}>60s</option>
            </select>
          </div>

          <button
            onClick={exportarExcel}
            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center gap-2"
          >
            <FiDownload /> Excel
          </button>
          <button
            onClick={exportarPDF}
            className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium flex items-center gap-2"
          >
            <FiDownload /> PDF
          </button>
          <button
            onClick={cargar}
            className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium flex items-center gap-2"
            title="Recargar módulo"
          >
            <FiRefreshCw /> Actualizar
          </button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat title="Incidencias (filtro)" value={resumen.total} />
        <Stat title="Con imágenes" value={resumen.conImg} />
        <Stat title="Con firma" value={resumen.conFirma} />
        <Stat title="Con PDF" value={resumen.conPdf} />
      </div>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-4">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <FiFilter /> Filtros
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
          >
            {filtersOpen ? "Ocultar" : "Mostrar"}
          </button>
        </div>

        {filtersOpen && (
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              {/* Busqueda */}
              <div className="lg:col-span-5">
                <label className="block text-xs font-medium text-slate-600 mb-1">Búsqueda</label>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <FiSearch className="text-slate-500" />
                  <input
                    className="w-full bg-transparent outline-none text-sm"
                    placeholder="ID, lugar, municipio, oficina, reportante, descripción..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  {q ? (
                    <button onClick={() => setQ("")} className="p-1 rounded hover:bg-white">
                      <FiX />
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Rango fechas */}
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Fecha inicio</label>
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={fechaIni}
                  onChange={(e) => setFechaIni(e.target.value)}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Fecha fin</label>
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>

              {/* Municipio */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Municipio</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={municipio}
                  onChange={(e) => setMunicipio(e.target.value)}
                >
                  <option value="">Todos</option>
                  {options.municipios.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Oficina */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Oficina</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={oficina}
                  onChange={(e) => setOficina(e.target.value)}
                >
                  <option value="">Todas</option>
                  {options.oficinas.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <option value="">Todos</option>
                  {options.tipos.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Riesgo */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Riesgo</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={riesgo}
                  onChange={(e) => setRiesgo(e.target.value)}
                >
                  <option value="">Todos</option>
                  {options.riesgos.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cargo */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Cargo</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                >
                  <option value="">Todos</option>
                  {options.cargos.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reportado por */}
              <div className="lg:col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">Reportado por</label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  value={reportadoPor}
                  onChange={(e) => setReportadoPor(e.target.value)}
                >
                  <option value="">Todos</option>
                  {options.reportados.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              {/* Flags */}
              <div className="lg:col-span-12 flex flex-wrap items-center gap-4 mt-1">
                {/*<Toggle label="Solo con imágenes" value={soloConImagenes} onChange={setSoloConImagenes} />
                <Toggle label="Solo con firma" value={soloConFirma} onChange={setSoloConFirma} />
                <Toggle label="Solo con PDF" value={soloConPdf} onChange={setSoloConPdf} />*/}

                <button
                  onClick={limpiar}
                  className="ml-auto px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {loading ? "Cargando..." : `${filtered.length} resultados`}
          </div>
          {err ? <div className="text-sm text-red-600 font-semibold">{err}</div> : null}
        </div>

        <div className="overflow-auto max-h-[70vh] relative">
          <table className="min-w-[980px] w-full text-xs">
            <thead className="bg-slate-900 text-white sticky top-0 z-30">
              <tr>
                <th className="px-3 py-2 text-left whitespace-nowrap">Fecha</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">ID</th>
                <th className="px-3 py-2 text-left">Ubicación</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Tipo</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Riesgo</th>
                <th className="px-3 py-2 text-left">Reportado por</th>
              {/*   <th className="px-3 py-2 text-center whitespace-nowrap">Imgs</th>
                <th className="px-3 py-2 text-center whitespace-nowrap">Firma</th>
                <th className="px-3 py-2 text-center whitespace-nowrap">PDF</th>*/}
                <th className="px-3 py-2 text-right sticky right-0 bg-slate-900 z-40 whitespace-nowrap">
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                    Cargando evidencias…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                    Sin resultados con los filtros actuales.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 hover:bg-slate-50 odd:bg-white even:bg-slate-50/30"
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmt(r.createdAt || r.fechaReporte || r.fecha)}
                    </td>

                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{r.id}</td>

                    <td className="px-3 py-2 min-w-[260px]">
                      <div className="font-medium text-slate-800">
                        {r.oficina || "—"} <span className="text-slate-400">·</span> {r.municipio || "—"}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[420px]">
                        {r.lugarReporte || "—"}
                      </div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">{r.tipoIncidencia || "—"}</td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <BadgeRiesgo value={r.nivelRiesgo || "N/D"} />
                    </td>

                    <td className="px-3 py-2 min-w-[160px]">
                      <div className="text-slate-800">{r.reportadoPor || "—"}</div>
                      <div className="text-[11px] text-slate-500">{r.cargo || ""}</div>
                    </td>

                  {/*   <td className="px-3 py-2 text-center whitespace-nowrap">{countImages(r)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{hasFirma(r) ? "✅" : "—"}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{hasPdf(r) ? "✅" : "—"}</td>*/}

                    <td
                      className="px-3 py-2 sticky right-0 bg-white z-20 whitespace-nowrap
                                 shadow-[-12px_0_12px_-12px_rgba(0,0,0,0.25)]"
                    >
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => abrirPdf(r.id)}
                          className="p-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                          title="Abrir PDF"
                        >
                          <FiFileText />
                        </button>

                        <a
                          href={`/incidencias/${r.id}`}
                          className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50"
                          title="Ver detalle"
                        >
                          <FiExternalLink />
                        </a>

                        <button
                          onClick={() => eliminar(r.id)}
                          disabled={deletingId === r.id}
                          className={`p-2 rounded-xl border ${
                            deletingId === r.id
                              ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                              : "border-red-200 text-red-700 hover:bg-red-50"
                          }`}
                          title="Eliminar incidencia"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ===== UI atoms ===== */

function Stat({ title, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      {label}
    </label>
  );
}

function BadgeRiesgo({ value }) {
  const v = String(value || "").toUpperCase();
  const base =
    "inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap";
  if (v.includes("CRIT")) return <span className={`${base} bg-red-50 text-red-700`}>CRÍTICO</span>;
  if (v.includes("MED")) return <span className={`${base} bg-amber-50 text-amber-700`}>MEDIO</span>;
  if (v.includes("BAJ")) return <span className={`${base} bg-emerald-50 text-emerald-700`}>BAJO</span>;
  return <span className={`${base} bg-slate-100 text-slate-700`}>{value}</span>;
}

/* ===== helpers ===== */

function fmt(value) {
  if (!value) return "N/D";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    return `${d}/${m}/${String(y).slice(2)}`;
  }

  const d = new Date(value);
  if (isNaN(d.getTime())) return "N/D";

  return new Intl.DateTimeFormat("es-MX", {
    timeZone: "America/Cancun",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}
